import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Plus, Trash2, Filter, Download, Upload, Paperclip, X, Check, ChevronDown, Cog, Loader2, FileText, CheckCircle2, XCircle, Clock, Wallet, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DatePickerUK } from "@/components/app/DatePickerUK";
import { UserPicker } from "@/components/app/UserPicker";

const STATUS_META = {
  draft: { label: "Draft · confirm to submit", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40", Icon: Clock },
  submitted: { label: "Submitted", cls: "bg-blue-500/15 text-blue-300 border-blue-500/30", Icon: Clock },
  approved: { label: "Approved · awaiting payment", cls: "bg-[#83B68B]/15 text-[#83B68B] border-[#83B68B]/30", Icon: CheckCircle2 },
  processed: { label: "Paid", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", Icon: Wallet },
  rejected: { label: "Rejected", cls: "bg-red-500/15 text-red-300 border-red-500/40", Icon: XCircle },
};

const csvCell = (v) => {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const humanSize = (bytes) => {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/** File upload input — POSTs to /expenses/upload-attachment, calls onUploaded(storage_path, meta). */
function InvoiceReader({ onParsed }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const read = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const [{ data }, att] = await Promise.all([
        api.post("/expenses/read-invoice", fd, { headers: { "Content-Type": "multipart/form-data" } }),
        (() => { const fd2 = new FormData(); fd2.append("file", f); return api.post("/expenses/upload-attachment", fd2, { headers: { "Content-Type": "multipart/form-data" } }).catch(() => null); })(),
      ]);
      onParsed(data, att?.data?.storage_path || null);
      toast.success(`Read invoice${data.supplier ? ` from ${data.supplier}` : ""} — please check the details`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't read the invoice"); }
    finally { setBusy(false); }
  };
  return (
    <div className="card-tactical rounded-md p-3 flex flex-wrap items-center gap-3" data-testid="expense-invoice-reader">
      <div className="text-xs flex-1 min-w-[200px]"><span className="font-semibold text-[#83B68B]">Ernie can read your invoice.</span> <span className="opacity-70">Upload a PDF or photo and the amount, date, supplier and invoice number are filled in for you to check.</span></div>
      <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid="expense-read-invoice-btn"
        className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-[#83B68B]/50 text-[#83B68B] hover:bg-[#83B68B] hover:text-white inline-flex items-center gap-1.5 disabled:opacity-40">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />} {busy ? "Reading…" : "Read invoice"}
      </button>
      <input ref={ref} type="file" accept=".pdf,image/*" onChange={read} className="hidden" />
    </div>
  );
}

function AttachmentField({ value, onChange, label, testId }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = () => ref.current?.click();
  const upload = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/expenses/upload-attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.storage_path, data);
      toast.success("Attached");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2">
      <button type="button" onClick={pick} disabled={busy} data-testid={testId}
        className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] inline-flex items-center gap-1.5 disabled:opacity-40">
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
        {value ? "Replace" : (label || "Attach")}
      </button>
      {value && <span className="text-[10px] opacity-70 truncate max-w-[180px]" title={value}>Attached ✓</span>}
      {value && <button type="button" onClick={() => onChange("", null)} className="opacity-50 hover:opacity-100 hover:text-red-400 p-1" title="Remove"><X className="w-3 h-3" /></button>}
      <input ref={ref} type="file" onChange={upload} className="hidden" accept=".pdf,.docx,image/*" />
    </div>
  );
}

/** Custom-field renderer driven by the category's required_fields spec. */
function CustomFieldInput({ spec, value, onChange, testId }) {
  if (spec.type === "file") {
    return <AttachmentField value={value} onChange={(p) => onChange(p)} label={spec.name} testId={testId} />;
  }
  if (spec.type === "number") {
    return <Input type="number" step="0.01" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />;
  }
  if (spec.type === "date") {
    return <DatePickerUK value={value || ""} onChange={onChange} testId={testId} />;
  }
  return <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />;
}

const emptyLine = () => ({ _key: Math.random().toString(36).slice(2), category_id: "", amount: "", description: "", date_of_expense: "", custom_fields: {}, receipts: [] });

export default function ExpenseReclaim() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isApprover = isAdmin || user?.is_expense_approver === true || user?.role === "treasurer";
  const isTreasurer = isAdmin || user?.is_treasurer === true || user?.role === "treasurer";

  const [tab, setTab] = useState("mine");
  const [categories, setCategories] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Dialogs
  const [newOpen, setNewOpen] = useState(false);
  const [detailClaim, setDetailClaim] = useState(null);
  const [editClaim, setEditClaim] = useState(null);
  const [catAdminOpen, setCatAdminOpen] = useState(false);

  const loadCategories = async () => {
    try { const { data } = await api.get("/expenses/categories"); setCategories(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const loadClaims = async (scope = tab) => {
    setLoading(true);
    try { const { data } = await api.get("/expenses/claims", { params: { scope } }); setClaims(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadCategories(); }, []);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  useEffect(() => { setSelected(new Set()); }, [tab]);
  const [batchRef, setBatchRef] = useState("");
  const bulkPaid = async () => {
    if (!window.confirm(`Mark ${selected.size} claim(s) as paid${batchRef ? ` (batch ${batchRef})` : ""}? Each claimant will be emailed.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/expenses/claims/treasurer/bulk-process", { ids: [...selected], batch_reference: batchRef || null, note: batchRef ? `Paid in bank batch ${batchRef}` : null });
      toast.success(`${data.processed} claim${data.processed === 1 ? "" : "s"} marked as paid${data.skipped.length ? ` · ${data.skipped.length} skipped` : ""}`);
      setSelected(new Set()); setBatchRef(""); loadClaims();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBulkBusy(false); }
  };
  const downloadPaymentRun = async (category, onlySelected = false) => {
    try {
      const params = { ...(category ? { category } : {}), ...(onlySelected && selected.size ? { ids: [...selected].join(",") } : {}) };
      const { data, headers } = await api.get("/expenses/treasurer/payment-run.csv", { params, responseType: "blob" });
      const name = (headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "payment-run.csv";
      const url = URL.createObjectURL(data); const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
      toast.success("Payment run downloaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Export failed"); }
  };
  useEffect(() => { loadClaims(tab); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  const catNameById = useMemo(() => Object.fromEntries(categories.map(c => [c.id, c.name])), [categories]);

  const filtered = useMemo(() => claims.filter(c => {
    if (statusFilter !== "all" && c.status !== statusFilter) return false;
    if (catFilter !== "all" && !c.lines?.some(l => l.category_id === catFilter)) return false;
    if (dateFrom && (c.created_at?.slice(0, 10) || "") < dateFrom) return false;
    if (dateTo && (c.created_at?.slice(0, 10) || "") > dateTo) return false;
    if (q) {
      const needle = q.toLowerCase();
      const hay = [c.ref, c.invoice_id, c.submitter_name, c.payee_name, c.notes, c.payment_method, c.payment_reference, ...(c.lines || []).map(l => `${l.category_name} ${l.description || ""}`)].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  }), [claims, statusFilter, catFilter, dateFrom, dateTo, q]);

  const activeFilterCount = [q, catFilter !== "all" ? catFilter : "", statusFilter !== "all" ? statusFilter : "", dateFrom, dateTo].filter(Boolean).length;
  const clearFilters = () => { setQ(""); setCatFilter("all"); setStatusFilter("all"); setDateFrom(""); setDateTo(""); };

  const exportCsv = () => {
    if (filtered.length === 0) return toast.error("No claims to export with these filters");
    const rows = filtered.flatMap(c => (c.lines || []).map(l => [
      c.ref, c.status, c.created_at?.slice(0, 10) || "", c.submitter_name, c.payee_name || "",
      l.category_name || catNameById[l.category_id] || "",
      l.date_of_expense || "", l.description || "", Number(l.amount || 0).toFixed(2),
      c.payment_method || "", c.payment_reference || "", c.invoice_id || "", c.notes || "",
    ]));
    const headers = ["Ref", "Status", "Raised", "Submitter", "Payee", "Category", "Date of expense", "Description", "Amount (£)", "Payment method", "Payment ref", "Invoice ID", "Notes"];
    const csv = [headers, ...rows].map(r => r.map(csvCell).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `expense-claims-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} row(s)`);
  };

  const cancelClaim = async (c) => {
    if (!window.confirm(`Remove claim ${c.ref} from the queue? This can't be undone.`)) return;
    try { await api.delete(`/expenses/claims/${c.id}`); toast.success("Claim removed"); setDetailClaim(null); loadClaims(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Reimbursements</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2 leading-tight">Expense Reclaim</h1>
          <p className="opacity-60 mt-2 text-sm max-w-xl">Raise an expense claim, attach receipts, and track it through Approver → Treasurer until it's paid.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && (
            <button onClick={() => setCatAdminOpen(true)} data-testid="expense-manage-categories" className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] inline-flex items-center gap-1.5">
              <Cog className="w-3 h-3" /> Categories
            </button>
          )}
          <button onClick={exportCsv} data-testid="expense-export-csv" className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] inline-flex items-center gap-1.5">
            <Download className="w-3 h-3" /> Export ({filtered.length})
          </button>
          <button onClick={() => setNewOpen(true)} data-testid="expense-new-btn" className="btn-volt px-5 py-2.5 rounded-md inline-flex items-center gap-2 text-sm">
            <Plus className="w-4 h-4" /> New claim
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex flex-wrap gap-1 border-b border-border">
        {[
          { id: "mine", label: "My claims" },
          isApprover && { id: "approver", label: "Awaiting my approval" },
          isTreasurer && { id: "treasurer", label: "Awaiting payment" },
          isAdmin && { id: "all", label: "All claims" },
        ].filter(Boolean).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`expense-tab-${t.id}`}
            className={`px-4 py-2.5 text-xs tracking-widest uppercase font-display transition-colors border-b-2 -mb-px ${tab === t.id ? "text-[#83B68B] border-[#83B68B]" : "border-transparent opacity-60 hover:opacity-100"}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === "treasurer" && isTreasurer && (
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="treasurer-payment-run">
          <span className="text-[10px] tracking-widest uppercase opacity-60">Payment run</span>
          <button onClick={() => downloadPaymentRun()} data-testid="payment-run-all" className="text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] inline-flex items-center gap-1.5"><Download className="w-3 h-3" /> All approved (CSV)</button>
          <button onClick={() => downloadPaymentRun("referee")} data-testid="payment-run-referees" className="text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] inline-flex items-center gap-1.5"><Download className="w-3 h-3" /> Referee fees only (CSV)</button>
          <span className="text-xs opacity-50">One row per approved claim with payee &amp; payment details — ready for a BACS batch.</span>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setSelected(selected.size === filtered.length ? new Set() : new Set(filtered.map(c => c.id)))} data-testid="bulk-select-all" className="text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-border hover:border-[#83B68B]">{selected.size === filtered.length && filtered.length ? "Clear" : "Select all"}</button>
            <input value={batchRef} onChange={e => setBatchRef(e.target.value)} placeholder="Bank batch ref (optional)" data-testid="bulk-batch-ref" className="bg-background border border-border rounded px-2 py-1 text-xs w-44" />
            <button onClick={() => downloadPaymentRun(null, true)} disabled={!selected.size} data-testid="bulk-batch-csv" className="text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-border hover:border-[#83B68B] disabled:opacity-40">Batch CSV ({selected.size})</button>
            <button onClick={bulkPaid} disabled={!selected.size || bulkBusy} data-testid="bulk-mark-paid" className="btn-volt text-[10px] tracking-widest uppercase px-3 py-1.5 rounded disabled:opacity-40 inline-flex items-center gap-1.5"><Check className="w-3 h-3" /> Mark {selected.size || ""} as paid</button>
          </div>
        </div>
      )}
      <div className="card-tactical rounded-md p-5 mb-6" data-testid="expense-filters">
        <div className="flex items-baseline justify-between mb-3">
          <div className="text-[10px] tracking-widest uppercase opacity-60 inline-flex items-center gap-2">
            <Filter className="w-3 h-3" /> Filters {activeFilterCount > 0 && <span className="text-[#83B68B]">· {activeFilterCount} active</span>}
          </div>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} data-testid="expense-clear-filters" className="text-[10px] tracking-widest uppercase opacity-60 hover:opacity-100 hover:text-red-400">Clear all</button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2">
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Search</Label>
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Ref, payee, category, notes…" className="mt-1.5" data-testid="expense-search" />
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Category</Label>
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger className="mt-1.5" data-testid="expense-cat-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Status</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="mt-1.5" data-testid="expense-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="submitted">Submitted</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="processed">Paid</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">From</Label>
            <div className="mt-1.5"><DatePickerUK value={dateFrom} onChange={setDateFrom} testId="expense-date-from" /></div>
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">To</Label>
            <div className="mt-1.5"><DatePickerUK value={dateTo} onChange={setDateTo} testId="expense-date-to" /></div>
          </div>
        </div>
      </div>

      {/* Claims list */}
      {loading ? (
        <div className="opacity-60 font-display tracking-widest">LOADING…</div>
      ) : filtered.length === 0 ? (
        <div className="card-tactical rounded-md p-10 text-center opacity-60" data-testid="expense-empty-state">
          {tab === "mine" ? "You haven't raised any claims yet. Hit 'New claim' to get started." : "Nothing in this queue right now."}
        </div>
      ) : (
        <div className="space-y-3" data-testid="expense-list">
          {filtered.map(c => {
            const meta = STATUS_META[c.status] || STATUS_META.submitted;
            const Icon = meta.Icon;
            const canCancel = ["submitted", "draft"].includes(c.status) && (c.submitter_id === user?.id || isAdmin);
            return (
              <div key={c.id} className="flex items-start gap-3">
              {tab === "treasurer" && isTreasurer && c.status === "approved" && (
                <input type="checkbox" checked={selected.has(c.id)} onChange={() => setSelected(s => { const n = new Set(s); n.has(c.id) ? n.delete(c.id) : n.add(c.id); return n; })} data-testid={`bulk-select-${c.id}`} className="mt-7 accent-[#83B68B] w-4 h-4 flex-shrink-0" />
              )}
              <button onClick={() => setDetailClaim(c)} data-testid={`expense-row-${c.id}`}
                className="flex-1 card-tactical rounded-md p-5 flex items-start gap-5 text-left hover:border-[#83B68B]/40 transition-colors">
                <div className="w-16 text-center flex-shrink-0">
                  <div className="font-display font-black text-2xl text-[#83B68B] leading-none">£{Number(c.total_amount || 0).toFixed(0)}</div>
                  <div className="text-[10px] tracking-[0.2em] uppercase opacity-60 mt-1">{c.ref}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] tracking-widest uppercase px-2 py-0.5 rounded border inline-flex items-center gap-1 ${meta.cls}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                    <span className="text-sm font-medium">{c.submitter_name}</span>
                    {c.payee_name && c.payee_name !== c.submitter_name && (
                      <span className="text-xs opacity-70">→ pay {c.payee_name}</span>
                    )}
                  </div>
                  <div className="text-xs opacity-70 mt-1 flex flex-wrap gap-x-3">
                    <span>{(c.lines || []).length} line{(c.lines || []).length === 1 ? "" : "s"} · £{Number(c.total_amount || 0).toFixed(2)}</span>
                    <span>{c.created_at ? format(parseISO(c.created_at), "d MMM yyyy") : ""}</span>
                    {c.invoice_id && <span data-testid={`expense-invoice-${c.id}`}>· Inv {c.invoice_id}</span>}
                    {(c.lines || []).slice(0, 3).map((l, i) => <span key={l.id || `${l.category_name}-${i}`}>· {l.category_name}</span>)}
                    {(c.lines || []).length > 3 && <span>· +{(c.lines || []).length - 3} more</span>}
                  </div>
                  {c.status === "draft" && (
                    <div className="mt-2 text-xs text-amber-300 leading-snug" data-testid={`expense-draft-hint-${c.id}`}>Auto-created from a game — open to check the amount and confirm.</div>
                  )}
                  {c.rejection_reason && (
                    <div className="mt-2 text-xs text-red-400 leading-snug">Rejected by {c.rejection_stage}: "{c.rejection_reason}"</div>
                  )}
                </div>
                {canCancel && (
                  <button
                    onClick={(e) => { e.stopPropagation(); cancelClaim(c); }}
                    data-testid={`expense-cancel-${c.id}`}
                    className="opacity-50 hover:opacity-100 hover:text-red-400 p-2 flex-shrink-0"
                    title="Cancel while still Submitted"
                  ><Trash2 className="w-4 h-4" /></button>
                )}
              </button>
              </div>
            );
          })}
        </div>
      )}

      <NewClaimDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        categories={categories.filter(c => c.active)}
        onCreated={() => { setNewOpen(false); loadClaims(); }}
      />
      <NewClaimDialog
        open={!!editClaim}
        editing={editClaim}
        onClose={() => setEditClaim(null)}
        categories={categories.filter(c => c.active)}
        onCreated={() => { setEditClaim(null); setDetailClaim(null); loadClaims(); }}
      />

      <DetailDialog
        claim={detailClaim}
        onClose={() => setDetailClaim(null)}
        onChanged={() => { loadClaims(); setDetailClaim(null); }}
        onAmend={(c) => setEditClaim(c)}
        onRemove={(c) => cancelClaim(c)}
        isApprover={isApprover}
        isTreasurer={isTreasurer}
      />

      <CategoriesDialog
        open={catAdminOpen}
        onClose={() => setCatAdminOpen(false)}
        categories={categories}
        onChanged={loadCategories}
      />
    </div>
  );
}

// ---------------- New claim dialog ----------------

function NewClaimDialog({ open, onClose, categories, onCreated, editing = null }) {
  const [payee, setPayee] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("BACS");
  const [paymentRef, setPaymentRef] = useState("");
  const [invoiceId, setInvoiceId] = useState("");
  const [notes, setNotes] = useState("");
  const [lines, setLines] = useState([emptyLine()]);
  const [busy, setBusy] = useState(false);
  const csvInput = useRef();

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setPayee(editing.payee_name || ""); setPaymentMethod(editing.payment_method || "BACS"); setPaymentRef(editing.payment_reference || "");
      setInvoiceId(editing.invoice_id || ""); setNotes(editing.notes || "");
      setLines((editing.lines || []).map(l => ({ ...emptyLine(), ...l, amount: l.amount != null ? String(l.amount) : "", custom_fields: l.custom_fields || {}, receipts: l.receipts || [] })));
      return;
    }
    setPayee(""); setPaymentMethod("BACS"); setPaymentRef(""); setInvoiceId(""); setNotes(""); setLines([emptyLine()]);
  }, [open, editing]);

  const total = useMemo(() => lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0), [lines]);

  const setLine = (idx, patch) => setLines(cur => cur.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const removeLine = (idx) => setLines(cur => cur.length === 1 ? [emptyLine()] : cur.filter((_, i) => i !== idx));
  const addLine = () => setLines(cur => [...cur, emptyLine()]);

  const submit = async () => {
    const cleaned = lines.map(l => ({
      category_id: l.category_id,
      amount: parseFloat(l.amount || 0),
      description: (l.description || "").trim() || null,
      date_of_expense: l.date_of_expense || null,
      custom_fields: Object.fromEntries(Object.entries(l.custom_fields || {}).filter(([, v]) => v && String(v).trim())),
      receipts: l.receipts || [],
    })).filter(l => l.category_id && l.amount > 0);
    if (cleaned.length === 0) return toast.error("Add at least one line with a category and amount");
    setBusy(true);
    try {
      const body = {
        payee_name: payee.trim() || null,
        payment_method: paymentMethod || null,
        payment_reference: paymentRef.trim() || null,
        invoice_id: invoiceId.trim() || null,
        notes: notes.trim() || null,
        lines: cleaned,
      };
      if (editing) {
        await api.put(`/expenses/claims/${editing.id}`, body);
        toast.success(`Claim ${editing.ref} updated`);
      } else {
        await api.post("/expenses/claims", body);
        toast.success("Claim submitted — an approver has been notified");
      }
      onCreated();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadCsv = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      if (payee.trim()) fd.append("payee_name", payee.trim());
      if (paymentMethod) fd.append("payment_method", paymentMethod);
      if (paymentRef.trim()) fd.append("payment_reference", paymentRef.trim());
      if (invoiceId.trim()) fd.append("invoice_id", invoiceId.trim());
      if (notes.trim()) fd.append("notes", notes.trim());
      const { data } = await api.post("/expenses/claims/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Claim ${data.created.ref} created with ${data.rows} line(s). Attach receipts by opening the claim.`);
      onCreated();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const headers = ["date", "category", "amount", "description"];
    const sample = [new Date().toISOString().slice(0, 10), categories[0]?.name || "General reimbursement", "12.50", "Cones from Argos"];
    const csv = [headers, sample].map(r => r.map(csvCell).join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "expense-claim-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const applyInvoice = (d, receiptPath) => {
    if (d.invoice_number) setInvoiceId(String(d.invoice_number));
    const cat = categories.find(c => c.name === d.suggested_category) || categories.find(c => /general/i.test(c.name)) || categories[0];
    const desc = [d.supplier, d.description].filter(Boolean).join(" — ");
    const receiptField = (cat?.required_fields || []).find(f => f.type === "file");
    const line = { ...emptyLine(), category_id: cat?.id || "", amount: d.total_amount != null ? String(Number(d.total_amount).toFixed(2)) : "", description: desc, date_of_expense: d.date || "",
      custom_fields: receiptField && receiptPath ? { [receiptField.name]: receiptPath } : {}, receipts: !receiptField && receiptPath ? [receiptPath] : [] };
    setLines(cur => (cur.length === 1 && !cur[0].category_id && !cur[0].amount) ? [line] : [...cur, line]);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl">{editing ? `Amend claim ${editing.ref}` : "New expense claim"}</DialogTitle>
          <DialogDescription className="text-xs opacity-70">
            {editing ? "Add, remove or change line items before the claim is processed. Your changes are recorded in the claim history." : "Add one or more lines. Each line's category can require its own extra fields (e.g. a receipt or a course certificate)."}
          </DialogDescription>
        </DialogHeader>

        <InvoiceReader onParsed={applyInvoice} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Payee (defaults to you)</Label>
            <Input value={payee} onChange={e => setPayee(e.target.value)} className="mt-1.5" data-testid="expense-payee" />
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Payment method</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger className="mt-1.5" data-testid="expense-payment-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="BACS">BACS</SelectItem>
                <SelectItem value="Cash">Cash</SelectItem>
                <SelectItem value="PayPal">PayPal</SelectItem>
                <SelectItem value="Cheque">Cheque</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Payment reference (sort code + account, PayPal handle, …)</Label>
            <Input value={paymentRef} onChange={e => setPaymentRef(e.target.value)} className="mt-1.5" data-testid="expense-payment-ref" placeholder="e.g. 12-34-56 / 12345678 · A. Coach" />
          </div>
          <div>
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Invoice ID (optional)</Label>
            <Input value={invoiceId} onChange={e => setInvoiceId(e.target.value)} className="mt-1.5" data-testid="expense-invoice-id" placeholder="Supplier invoice / receipt number" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[10px] tracking-widest uppercase opacity-70">Notes (context for approver)</Label>
            <Input value={notes} onChange={e => setNotes(e.target.value)} className="mt-1.5" data-testid="expense-notes" />
          </div>
        </div>

        <div className="mt-6 mb-2 flex items-baseline justify-between">
          <div className="text-[10px] tracking-widest uppercase opacity-60">Line items</div>
          <div className="text-xs opacity-70">Total: <span className="text-[#83B68B] font-bold">£{total.toFixed(2)}</span></div>
        </div>

        <div className="space-y-3" data-testid="expense-lines">
          {lines.map((line, idx) => {
            const cat = categories.find(c => c.id === line.category_id);
            return (
              <div key={line._key} className="card-tactical rounded-md p-4 space-y-3" data-testid={`expense-line-${idx}`}>
                <div className="flex items-start gap-2">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-6 gap-2">
                    <div className="md:col-span-3">
                      <Label className="text-[10px] tracking-widest uppercase opacity-70">Category</Label>
                      <Select value={line.category_id} onValueChange={(v) => setLine(idx, { category_id: v, custom_fields: {} })}>
                        <SelectTrigger className="mt-1.5" data-testid={`expense-line-cat-${idx}`}><SelectValue placeholder="Pick a category" /></SelectTrigger>
                        <SelectContent>
                          {categories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-[10px] tracking-widest uppercase opacity-70">Amount £</Label>
                      <Input type="number" step="0.01" min="0" value={line.amount} onChange={e => setLine(idx, { amount: e.target.value })} className="mt-1.5" data-testid={`expense-line-amount-${idx}`} />
                    </div>
                    <div>
                      <Label className="text-[10px] tracking-widest uppercase opacity-70">Date</Label>
                      <div className="mt-1.5"><DatePickerUK value={line.date_of_expense} onChange={(v) => setLine(idx, { date_of_expense: v })} testId={`expense-line-date-${idx}`} /></div>
                    </div>
                    <div className="md:col-span-6">
                      <Label className="text-[10px] tracking-widest uppercase opacity-70">Description</Label>
                      <Input value={line.description} onChange={e => setLine(idx, { description: e.target.value })} className="mt-1.5" data-testid={`expense-line-desc-${idx}`} placeholder="What was this for?" />
                    </div>
                  </div>
                  <button type="button" onClick={() => removeLine(idx)} className="opacity-60 hover:opacity-100 hover:text-red-400 p-2 mt-6" title="Remove line" data-testid={`expense-line-remove-${idx}`}>
                    <X className="w-4 h-4" />
                  </button>
                </div>

                {cat && cat.description && <div className="text-[10px] opacity-60 leading-relaxed border-l-2 border-[#83B68B]/40 pl-2">{cat.description}</div>}

                {cat && (cat.required_fields || []).length > 0 && (
                  <div className="border-t border-border pt-3 space-y-2">
                    <div className="text-[10px] tracking-widest uppercase text-[#83B68B]">Required for "{cat.name}"</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {cat.required_fields.map((rf, rfi) => (
                        <div key={rf.name}>
                          <Label className="text-[10px] tracking-widest uppercase opacity-70">
                            {rf.name} {rf.required && <span className="text-[#83B68B]">*</span>}
                          </Label>
                          <div className="mt-1.5">
                            <CustomFieldInput
                              spec={rf}
                              value={line.custom_fields?.[rf.name] || ""}
                              onChange={(v) => setLine(idx, { custom_fields: { ...line.custom_fields, [rf.name]: v } })}
                              testId={`expense-line-cf-${idx}-${rfi}`}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <button type="button" onClick={addLine} className="mt-3 text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] inline-flex items-center gap-1.5" data-testid="expense-add-line">
          <Plus className="w-3 h-3" /> Add another line
        </button>

        <div className="mt-6 flex items-center justify-between border-t border-border pt-4 flex-wrap gap-2 sticky bottom-0 bg-background/95 backdrop-blur pb-1">
          <div className="text-sm" data-testid="expense-running-total">Running total <span className="font-display font-black text-2xl text-[#83B68B] ml-2">£{total.toFixed(2)}</span> <span className="text-xs opacity-60 ml-1">· {lines.filter(l => l.category_id && parseFloat(l.amount) > 0).length} line{lines.filter(l => l.category_id && parseFloat(l.amount) > 0).length === 1 ? "" : "s"}</span></div>
          <div className="flex items-center gap-2">
            {!editing && (<>
            <button type="button" onClick={downloadTemplate} className="text-[10px] tracking-widest uppercase opacity-70 hover:opacity-100" data-testid="expense-csv-template">
              CSV template
            </button>
            <button type="button" onClick={() => csvInput.current?.click()} disabled={busy} className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] inline-flex items-center gap-1.5 disabled:opacity-40" data-testid="expense-csv-upload">
              <Upload className="w-3 h-3" /> Bulk CSV
            </button>
            <input ref={csvInput} type="file" accept=".csv" onChange={uploadCsv} className="hidden" />
            </>)}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose} className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border opacity-70 hover:opacity-100">Cancel</button>
            <button type="button" onClick={submit} disabled={busy} className="btn-volt px-5 py-2.5 rounded-md text-sm inline-flex items-center gap-2 disabled:opacity-40" data-testid="expense-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {editing ? "Save changes" : "Submit claim"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Detail dialog ----------------

function FragmentRow({ line: l, authedFileUrl }) {
  const hasCustom = Object.keys(l.custom_fields || {}).length > 0;
  return (
    <>
      <tr className="border-t border-border">
        <td className="px-3 py-2 font-medium">{l.category_name}</td>
        <td className="px-3 py-2 opacity-80">{l.description || "—"}</td>
        <td className="px-3 py-2 opacity-80">{l.date_of_expense || "—"}</td>
        <td className="px-3 py-2 text-right">£{Number(l.amount || 0).toFixed(2)}</td>
      </tr>
      {hasCustom && (
        <tr className="border-t border-border bg-muted/30">
          <td colSpan={4} className="px-3 py-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs">
              {Object.entries(l.custom_fields || {}).map(([k, v]) => (
                <div key={k} className="flex gap-2">
                  <span className="opacity-60">{k}:</span>
                  {typeof v === "string" && v.includes("/") ? (
                    <a href={authedFileUrl(v)} target="_blank" rel="noopener noreferrer" className="text-[#83B68B] hover:underline inline-flex items-center gap-1">
                      <FileText className="w-3 h-3" /> Open attachment
                    </a>
                  ) : (
                    <span>{v}</span>
                  )}
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DraftActions({ claim, onChanged }) {
  const [amount, setAmount] = useState(claim.total_amount ? String(Number(claim.total_amount).toFixed(2)) : "");
  const [notes, setNotes] = useState(claim.notes || "");
  const [method, setMethod] = useState(claim.payment_method || "BACS");
  const [ref, setRef] = useState(claim.payment_reference || "");
  const [busy, setBusy] = useState(false);
  const [delegateOpen, setDelegateOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [target, setTarget] = useState(null);
  useEffect(() => { if (delegateOpen && users.length === 0) api.get("/chat/users").then(r => setUsers(r.data)).catch(() => {}); }, [delegateOpen, users.length]);

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Enter the referee fee you paid");
    setBusy(true);
    try {
      await api.post(`/expenses/claims/${claim.id}/submit`, { amount: amt, notes: notes || null, payment_method: method || null, payment_reference: ref || null });
      toast.success("Submitted to the approver queue"); onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  const delegate = async () => {
    if (!target) return toast.error("Pick who should claim this");
    setBusy(true);
    try { await api.post(`/expenses/claims/${claim.id}/delegate`, { to_user_id: target }); toast.success("Handed over"); onChanged(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="border-t border-border pt-4 space-y-3" data-testid="expense-draft-actions">
      <Label className="text-[10px] tracking-widest uppercase text-amber-300">Confirm this referee expense</Label>
      <p className="text-xs opacity-70">This was created automatically when <b>{claim.referee_name}</b> was recorded on your game. Check the amount you actually paid, then submit — or hand it to whoever paid the referee.</p>
      <div className="grid sm:grid-cols-3 gap-2">
        <div><Label className="text-[10px] uppercase opacity-60">Amount paid (£)</Label><Input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} data-testid="expense-draft-amount" /></div>
        <div><Label className="text-[10px] uppercase opacity-60">Payment method</Label><Input value={method} onChange={e => setMethod(e.target.value)} placeholder="BACS / Cash / PayPal" data-testid="expense-draft-method" /></div>
        <div><Label className="text-[10px] uppercase opacity-60">Your payment details</Label><Input value={ref} onChange={e => setRef(e.target.value)} placeholder="Sort code / account or PayPal" data-testid="expense-draft-ref" /></div>
      </div>
      <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Note for the approver (optional)" data-testid="expense-draft-notes" />
      {delegateOpen && (
        <div className="rounded border border-border p-3 space-y-2" data-testid="expense-delegate-panel">
          <Label className="text-[10px] uppercase opacity-60">Who actually paid the referee?</Label>
          <UserPicker users={users} value={target} onChange={setTarget} placeholder="Pick a person" testId="expense-delegate-target" />
          <div className="flex justify-end"><button onClick={delegate} disabled={busy || !target} data-testid="expense-delegate-confirm" className="text-xs tracking-widest uppercase px-4 py-2 rounded border border-border hover:border-[#83B68B] disabled:opacity-40">Hand over</button></div>
        </div>
      )}
      <div className="flex gap-2 justify-end flex-wrap">
        <button onClick={() => setDelegateOpen(v => !v)} data-testid="expense-delegate-btn" className="text-xs tracking-widest uppercase px-4 py-2 rounded border border-border hover:border-[#83B68B] inline-flex items-center gap-1.5">Delegate to someone else</button>
        <button onClick={submit} disabled={busy} data-testid="expense-draft-submit" className="btn-volt px-4 py-2 rounded-md text-xs tracking-widest uppercase disabled:opacity-40 inline-flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Confirm & submit</button>
      </div>
    </div>
  );
}

function DetailDialog({ claim, onClose, onChanged, onAmend, onRemove, isApprover, isTreasurer }) {
  const { user } = useAuth();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setReason(""); }, [claim?.id]);
  if (!claim) return null;

  const authedFileUrl = (path) => {
    const token = typeof window !== "undefined" ? localStorage.getItem("access_token") : null;
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${path}`;
  };

  const act = async (endpoint, action) => {
    if (action === "reject" && !reason.trim()) return toast.error("Reason is required to reject");
    setBusy(true);
    try {
      await api.patch(`/expenses/claims/${claim.id}/${endpoint}`, { action, note: reason.trim() || null });
      toast.success(`Claim ${claim.ref} · ${action}${action.endsWith("e") ? "d" : "ed"}`);
      onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const meta = STATUS_META[claim.status] || STATUS_META.submitted;

  return (
    <Dialog open={!!claim} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl">{claim.ref} · £{Number(claim.total_amount || 0).toFixed(2)}</DialogTitle>
          <DialogDescription>
            <span className={`text-[10px] tracking-widest uppercase px-2 py-0.5 rounded border inline-flex items-center gap-1 ${meta.cls}`}>
              {meta.label}
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4" data-testid={`expense-detail-${claim.id}`}>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-[10px] tracking-widest uppercase opacity-60">Submitter</div><div>{claim.submitter_name}</div></div>
            <div><div className="text-[10px] tracking-widest uppercase opacity-60">Payee</div><div>{claim.payee_name || claim.submitter_name}</div></div>
            <div><div className="text-[10px] tracking-widest uppercase opacity-60">Payment method</div><div>{claim.payment_method || "—"}</div></div>
            <div><div className="text-[10px] tracking-widest uppercase opacity-60">Payment ref</div><div>{claim.payment_reference || "—"}</div></div>
            <div><div className="text-[10px] tracking-widest uppercase opacity-60">Invoice ID</div><div data-testid="expense-detail-invoice">{claim.invoice_id || "—"}</div></div>
            <div><div className="text-[10px] tracking-widest uppercase opacity-60">Notes</div><div>{claim.notes || "—"}</div></div>
          </div>

          {["draft", "submitted"].includes(claim.status) && (claim.submitter_id === user?.id || isApprover) && (
            <div className="flex flex-wrap gap-2" data-testid="expense-queue-actions">
              <button type="button" onClick={() => onAmend(claim)} data-testid="expense-amend-btn" className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B] inline-flex items-center gap-1.5">
                <Pencil className="w-3 h-3" /> Amend items
              </button>
              <button type="button" onClick={() => onRemove(claim)} data-testid="expense-remove-btn" className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-red-500/40 text-red-400 hover:bg-red-500/10 inline-flex items-center gap-1.5">
                <Trash2 className="w-3 h-3" /> Remove from queue
              </button>
              <span className="text-[11px] opacity-60 self-center">Editable until an approver processes it.</span>
            </div>
          )}

          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted text-[10px] tracking-widest uppercase opacity-60">
                <tr>
                  <th className="text-left px-3 py-2">Category</th>
                  <th className="text-left px-3 py-2">Description</th>
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-right px-3 py-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(claim.lines || []).map((l, i) => (
                  <FragmentRow key={l.id || i} line={l} authedFileUrl={authedFileUrl} />
                ))}
              </tbody>
            </table>
          </div>

          {claim.status_history && claim.status_history.length > 0 && (
            <div>
              <div className="text-[10px] tracking-widest uppercase opacity-60 mb-2">History</div>
              <div className="space-y-1.5">
                {claim.status_history.map((h, i) => (
                  <div key={`${h.at}-${i}`} className="text-xs flex flex-wrap gap-2">
                    <span className={`px-1.5 py-0.5 rounded border ${STATUS_META[h.status]?.cls || ""}`}>{STATUS_META[h.status]?.label || h.status}</span>
                    <span className="opacity-70">by {h.by_name} · {h.at ? format(parseISO(h.at), "d MMM HH:mm") : ""}</span>
                    {h.note && <span className="opacity-80 italic">"{h.note}"</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {claim.status === "draft" && (claim.submitter_id === user?.id || user?.role === "admin") && (
            <DraftActions claim={claim} onChanged={() => { onChanged(); onClose(); }} />
          )}

          {claim.status === "submitted" && isApprover && (
            <div className="border-t border-border pt-4 space-y-2">
              <Label className="text-[10px] tracking-widest uppercase text-[#83B68B]">Approver actions</Label>
              <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason (required if rejecting)" data-testid="expense-decision-note" />
              <div className="flex gap-2 justify-end">
                <button onClick={() => act("approver", "reject")} disabled={busy} data-testid="expense-reject-btn" className="text-xs tracking-widest uppercase px-4 py-2 rounded border border-red-500/50 text-red-400 hover:bg-red-500/10 disabled:opacity-40 inline-flex items-center gap-1.5">
                  <XCircle className="w-3.5 h-3.5" /> Reject
                </button>
                <button onClick={() => act("approver", "approve")} disabled={busy} data-testid="expense-approve-btn" className="btn-volt px-4 py-2 rounded-md text-xs tracking-widest uppercase disabled:opacity-40 inline-flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5" /> Approve
                </button>
              </div>
            </div>
          )}

          {claim.status === "approved" && isTreasurer && (
            <div className="border-t border-border pt-4 space-y-2">
              <Label className="text-[10px] tracking-widest uppercase text-[#83B68B]">Treasurer actions</Label>
              <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Payment reference or rejection reason" data-testid="expense-decision-note" />
              <div className="flex gap-2 justify-end">
                <button onClick={() => act("treasurer", "reject")} disabled={busy} data-testid="expense-reject-btn" className="text-xs tracking-widest uppercase px-4 py-2 rounded border border-red-500/50 text-red-400 hover:bg-red-500/10 disabled:opacity-40 inline-flex items-center gap-1.5">
                  <XCircle className="w-3.5 h-3.5" /> Reject
                </button>
                <button onClick={() => act("treasurer", "process")} disabled={busy} data-testid="expense-mark-paid-btn" className="btn-volt px-4 py-2 rounded-md text-xs tracking-widest uppercase disabled:opacity-40 inline-flex items-center gap-1.5">
                  <Wallet className="w-3.5 h-3.5" /> Mark paid
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Category admin dialog ----------------

const emptyCategory = () => ({ name: "", description: "", active: true, required_fields: [{ name: "Receipt", type: "file", required: true }] });

function CategoriesDialog({ open, onClose, categories, onChanged }) {
  const [editing, setEditing] = useState(null);

  const openNew = () => setEditing({ ...emptyCategory(), _isNew: true });
  const openEdit = (c) => setEditing({ ...c, required_fields: [...(c.required_fields || [])] });
  const closeEdit = () => setEditing(null);

  const saveCat = async () => {
    if (!editing.name.trim()) return toast.error("Name required");
    const payload = {
      name: editing.name.trim(),
      description: (editing.description || "").trim() || null,
      active: editing.active,
      required_fields: (editing.required_fields || []).filter(f => f.name.trim()).map(f => ({ name: f.name.trim(), type: f.type, required: !!f.required })),
    };
    try {
      if (editing._isNew) await api.post("/admin/expenses/categories", payload);
      else await api.put(`/admin/expenses/categories/${editing.id}`, payload);
      toast.success("Category saved");
      closeEdit();
      onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const deleteCat = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? (Will be deactivated if any claim uses it.)`)) return;
    try {
      const { data } = await api.delete(`/admin/expenses/categories/${c.id}`);
      toast.success(data.deactivated ? "Category deactivated (still referenced by claims)" : "Category deleted");
      onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl">Expense categories</DialogTitle>
            <DialogDescription className="text-xs opacity-70">Add categories, tick which extra fields are required. File-type fields become an upload button on the claim form.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {categories.length === 0 ? (
              <div className="text-sm opacity-60">No categories yet.</div>
            ) : categories.map(c => (
              <div key={c.id} className="card-tactical rounded-md p-3 flex items-center justify-between" data-testid={`expense-cat-row-${c.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium flex items-center gap-2">
                    {c.name}
                    {!c.active && <span className="text-[10px] tracking-widest uppercase opacity-60">Inactive</span>}
                  </div>
                  <div className="text-xs opacity-60">
                    {(c.required_fields || []).map(f => `${f.name}${f.required ? "" : "?"} (${f.type})`).join(" · ") || "No required fields"}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => openEdit(c)} className="text-[10px] tracking-widest uppercase px-3 py-1.5 rounded border border-border hover:border-[#83B68B] hover:text-[#83B68B]" data-testid={`expense-cat-edit-${c.id}`}>Edit</button>
                  <button onClick={() => deleteCat(c)} className="opacity-60 hover:opacity-100 hover:text-red-400 p-1.5" data-testid={`expense-cat-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={openNew} className="btn-volt px-5 py-2 rounded-md text-xs tracking-widest uppercase inline-flex items-center gap-1.5" data-testid="expense-cat-new-btn">
              <Plus className="w-3.5 h-3.5" /> New category
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(v) => { if (!v) closeEdit(); }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight text-2xl">{editing?._isNew ? "New category" : "Edit category"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] tracking-widest uppercase opacity-70">Name</Label>
                <Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} className="mt-1.5" data-testid="expense-cat-form-name" />
              </div>
              <div>
                <Label className="text-[10px] tracking-widest uppercase opacity-70">Description</Label>
                <Input value={editing.description || ""} onChange={e => setEditing({ ...editing, description: e.target.value })} className="mt-1.5" placeholder="Shown to users when they pick this category" data-testid="expense-cat-form-desc" />
              </div>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked })} className="w-4 h-4 accent-[#83B68B]" data-testid="expense-cat-form-active" />
                <span className="text-xs">Active</span>
              </label>
              <div className="border-t border-border pt-3">
                <div className="flex items-baseline justify-between mb-2">
                  <Label className="text-[10px] tracking-widest uppercase text-[#83B68B]">Required fields</Label>
                  <button type="button" onClick={() => setEditing({ ...editing, required_fields: [...(editing.required_fields || []), { name: "", type: "text", required: true }] })} className="text-[10px] tracking-widest uppercase opacity-70 hover:opacity-100" data-testid="expense-cat-form-add-field">+ add field</button>
                </div>
                <div className="space-y-2">
                  {(editing.required_fields || []).map((f, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`expense-cat-form-field-${i}`}>
                      <Input value={f.name} onChange={e => { const next = [...editing.required_fields]; next[i] = { ...next[i], name: e.target.value }; setEditing({ ...editing, required_fields: next }); }} placeholder="Field name (e.g. Course name)" className="col-span-6" />
                      <Select value={f.type} onValueChange={(v) => { const next = [...editing.required_fields]; next[i] = { ...next[i], type: v }; setEditing({ ...editing, required_fields: next }); }}>
                        <SelectTrigger className="col-span-3"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Text</SelectItem>
                          <SelectItem value="number">Number</SelectItem>
                          <SelectItem value="date">Date</SelectItem>
                          <SelectItem value="file">File / image</SelectItem>
                        </SelectContent>
                      </Select>
                      <label className="col-span-2 inline-flex items-center gap-1 cursor-pointer">
                        <input type="checkbox" checked={f.required} onChange={e => { const next = [...editing.required_fields]; next[i] = { ...next[i], required: e.target.checked }; setEditing({ ...editing, required_fields: next }); }} className="w-3.5 h-3.5 accent-[#83B68B]" />
                        <span className="text-[10px] tracking-widest uppercase opacity-70">Req</span>
                      </label>
                      <button type="button" onClick={() => setEditing({ ...editing, required_fields: editing.required_fields.filter((_, j) => j !== i) })} className="col-span-1 opacity-60 hover:opacity-100 hover:text-red-400 p-1"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button onClick={closeEdit} className="text-[10px] tracking-widest uppercase px-3 py-2 rounded border border-border opacity-70 hover:opacity-100">Cancel</button>
                <button onClick={saveCat} className="btn-volt px-5 py-2 rounded-md text-xs tracking-widest uppercase" data-testid="expense-cat-form-save">Save</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
